import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Clock, ArrowRight, Timer, User } from "lucide-react"; // ⬅️ Added
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { Button } from "../../../../components/ui/button";
import { StepFooterNav } from "../../shared/NavigationFooter";

export function Step2({ nextStep, preStep, currentStep, totalSteps }) {
  const {
    jobPostProgress,
    setScheduledTime,
    setServiceDuration,
    setReferredBy,
  } = useJobPostProgressContext();

  // helpers
  const ctxToInputTime = (st) => {
    if (!st || !st.hour) return "";
    const hour12 = parseInt(String(st.hour).replace(/^0+/, "") || "0", 10);
    const minute = String(st.minute ?? "00").padStart(2, "0");
    const period = (st.period || "AM").toUpperCase();
    let hour24 = hour12 % 12;
    if (period === "PM") hour24 = (hour24 + 12) % 24;
    if (period === "AM" && hour12 === 12) hour24 = 0;
    return `${String(hour24).padStart(2, "0")}:${minute}`;
  };

  const inputTimeTo12 = (time24) => {
    if (!time24) return null;
    const [hStr = "09", mStr = "00"] = String(time24).split(":");
    const hNum = parseInt(hStr, 10);
    const period = hNum >= 12 ? "PM" : "AM";
    let h12 = hNum % 12;
    if (h12 === 0) h12 = 12;
    return [String(h12).padStart(2, "0"), String(mStr).padStart(2, "0"), period];
  };

  // state
  const [startTime, setStartTime] = useState(() =>
    ctxToInputTime(jobPostProgress?.scheduledTime)
  );
  const [selectedDuration, setSelectedDuration] = useState(
    jobPostProgress?.serviceDuration ?? ""
  );
  const [selectedReferredBy, setSelectedReferredBy] = useState(
    jobPostProgress?.referredBy ?? ""
  );

  const durations = [
    "Less than 30 min",
    "30-60 min",
    "60-90 min",
    "90-120 min",
    "More than 120 min",
  ];

  // handlers
  const handleNext = () => {
    let hour12Str, minuteStr, periodStr;
    const converted = inputTimeTo12(startTime);
    if (converted) {
      [hour12Str, minuteStr, periodStr] = converted;
    } else {
      hour12Str = "09";
      minuteStr = "00";
      periodStr = "AM";
    }

    setScheduledTime(hour12Str, minuteStr, periodStr);
    setServiceDuration(selectedDuration);
    setReferredBy(selectedReferredBy);

    nextStep();
  };

  const handleBack = () => {
    preStep();
  };

  useEffect(() => {
    setStartTime(ctxToInputTime(jobPostProgress?.scheduledTime));
    setSelectedDuration(jobPostProgress?.serviceDuration ?? "");
    setSelectedReferredBy(jobPostProgress?.referredBy ?? "");
  }, [jobPostProgress]);

  // render
  return (
    <div className="min-h-screen py-10">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold text-black">Timing & Duration</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            When do you need the service and how long will it take?
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border rounded-lg shadow-sm p-6 max-w-2xl mx-auto">
          {/* Start Time */}
          <div className="space-y-3 mb-6">
            <label className="flex items-center gap-2 text-lg font-medium">
              <Clock className="w-5 h-5" />
              Service Start Time
            </label>
            <input
              type="time"
              step="60"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full h-12 text-base px-4 border rounded-lg bg-zinc-50"
            />
          </div>

          {/* Duration */}
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-lg font-medium">
              <Timer className="w-5 h-5" />
              Service Duration
            </label>
            <p className="text-sm text-stone-500">
              Duration is an estimate and may vary.
            </p>
            <div className="space-y-2">
              {durations.map((duration) => {
                const active = selectedDuration === duration;
                return (
                  <motion.div
                    key={duration}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedDuration(duration)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-300 flex items-center justify-between ${active
                      ? "border-black/50 bg-secondary text-white"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                  >
                    <span className="text-base">{duration}</span>
                    <div
                      className={`w-3.5 h-3.5 rounded-full border-2 ${active ? "bg-white border-black/50" : "border-gray-300"
                        }`}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Referred By */}
          <div className="space-y-3 mt-6">
            <label className="flex items-center gap-2 text-lg font-medium">
              <User className="w-5 h-5" />
              Referred By (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter referrer's name"
              value={selectedReferredBy}
              onChange={(e) => setSelectedReferredBy(e.target.value)}
              className="w-full h-11 px-4 border rounded-lg text-base"
            />
          </div>
        </div>

        {/* Footer Navigation */}
        <StepFooterNav
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={handleBack}
          onNext={handleNext}
          nextDisabled={!selectedDuration}
        />
      </div>
    </div>
  );
}
